import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { Kicker, LineReveal, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { DIVISION_LIST } from "@/data/divisions";

const HERO_BG =
  "https://images.unsplash.com/photo-1592309905620-e5b59f6dcb98?crop=entropy&cs=srgb&fm=jpg&q=85";

const MANIFESTO = [
  {
    n: "01",
    title: "Excelencia en cada detalle",
    desc: "Desde la ruta más corta hasta la misión más compleja, cada operación se ejecuta con el mismo nivel de exigencia.",
  },
  {
    n: "02",
    title: "Precisión operativa",
    desc: "Itinerarios, reservas y traslados coordinados al minuto. Nada queda al azar cuando representamos a tu organización.",
  },
  {
    n: "03",
    title: "Discreción absoluta",
    desc: "La confianza de figuras públicas, equipos y diplomáticos se sostiene sobre un principio: lo que ocurre con nosotros, queda con nosotros.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <main className="bg-void" data-testid="page-home">
      <section
        ref={heroRef}
        className="noise-overlay relative flex min-h-screen items-center overflow-hidden"
      >
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <img src={HERO_BG} alt="Grupo Disimil" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-void via-void/80 to-void/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-void/60" />
        </motion.div>

        <motion.div
          style={{ opacity: fade }}
          className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-24 sm:px-6 lg:px-8"
        >
          <Reveal>
            <Kicker className="text-gold/80">Consorcio Empresarial — Venezuela</Kicker>
          </Reveal>
          <h1
            data-testid="home-hero-title"
            className="mt-8 font-serif text-4xl font-bold leading-[1.05] tracking-tight text-slate-100 sm:text-5xl lg:text-7xl"
          >
            <LineReveal
              lines={[
                "Bienvenido a",
                <span key="gd" className="text-gold-gradient">
                  Grupo Disimil.
                </span>,
              ]}
              delay={0.25}
            />
          </h1>
          <Reveal delay={0.7}>
            <p className="mt-8 font-serif text-xl italic text-gold-light/90 sm:text-2xl">
              Soluciones integrales y exclusivas al más alto nivel.
            </p>
            <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-slate-300 sm:text-base">
              Somos un consorcio empresarial con más de 25 años de experiencia, diseñado para
              ofrecer excelencia en cada detalle. Selecciona el área de tu interés para conocer
              nuestros servicios especializados.
            </p>
            <div
              className="mt-8 inline-flex items-center gap-4 border border-gold/30 bg-void/40 px-6 py-3 backdrop-blur-sm"
              data-testid="home-experience-badge"
            >
              <span className="font-display text-2xl font-bold text-gold">+25</span>
              <span className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.24em] text-slate-400">
                Años de experiencia
                <br />
                al más alto nivel
              </span>
            </div>
          </Reveal>
        </motion.div>

        <motion.div
          style={{ opacity: fade }}
          className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2 text-slate-500">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Desliza</span>
            <ChevronDown className="h-4 w-4 animate-scroll-cue text-gold" />
          </div>
        </motion.div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <Reveal>
            <Kicker className="text-gold/80">Nuestras Divisiones</Kicker>
            <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-5xl">
              Cuatro casas,
              <br />
              un mismo estándar.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm font-light text-slate-400">
              Cada división opera con autonomía y especialización, bajo la garantía de excelencia
              del consorcio.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {DIVISION_LIST.map((d, i) => (
            <Reveal key={d.slug} delay={i * 0.08}>
              <Link
                to={d.path}
                data-testid={`home-card-${d.slug === "garaje" ? "ciudad-garaje" : d.slug}`}
                className="group relative block min-h-[380px] overflow-hidden border border-line bg-ink transition-colors duration-500 lg:min-h-[440px]"
                style={{ "--accent": d.accent }}
              >
                <img
                  src={d.hero}
                  alt={d.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-40 transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void via-void/50 to-transparent" />
                <div
                  className="absolute inset-x-0 top-0 h-px opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  style={{ background: `linear-gradient(90deg, transparent, ${d.accent}, transparent)` }}
                />
                <div className="relative flex h-full min-h-[380px] flex-col justify-end p-8 lg:min-h-[440px] lg:p-10">
                  <span
                    className="font-mono text-xs tracking-[0.3em]"
                    style={{ color: d.accent }}
                  >
                    {d.index} — {d.tag}
                  </span>
                  <h3 className="mt-4 font-serif text-3xl font-semibold text-slate-100 sm:text-4xl">
                    {d.name}
                  </h3>
                  <p className="mt-3 max-w-md font-serif text-base italic text-slate-300">
                    {d.tagline}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-slate-400 transition-colors duration-300 group-hover:text-slate-100">
                    Explorar división
                    <ArrowUpRight
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                      style={{ color: d.accent }}
                    />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section
        className="border-t border-line bg-[#0A0E14]"
        data-testid="home-manifesto-section"
      >
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
          <Reveal>
            <Kicker className="text-gold/80">El Manifiesto</Kicker>
          </Reveal>
          <div className="mt-10">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1}>
                <div className="grid gap-6 border-t border-line py-10 lg:grid-cols-[auto_1fr_1.2fr] lg:items-baseline lg:gap-16 lg:py-14">
                  <span className="outline-number font-serif text-6xl font-bold lg:text-8xl">
                    {m.n}
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-slate-100 sm:text-3xl">
                    {m.title}
                  </h3>
                  <p className="max-w-lg text-sm font-light leading-relaxed text-slate-400 sm:text-base">
                    {m.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
